/**
 * Worksheet Apps — NGXS action set (6th TMS app type).
 *
 * Action discriminators are {@code [WorksheetApp] <Name>} so devtools and the
 * action stream stay distinct from {@code [TsmApp]} / {@code [ReportApp]} etc.
 */

import { WorksheetMode, WorksheetSaveSheet, WorksheetSheet } from '../../models/worksheet-app.model';

export class InitWorksheetAppState {
	static readonly type: string = '[WorksheetApp] InitWorksheetAppState';
	constructor(
		public readonly appName: string,
		public readonly profileCd?: string
	) {}
}

export class ClearWorksheetAppState {
	static readonly type: string = '[WorksheetApp] ClearWorksheetAppState';
}

/** Fetch the available-profile list ({@code ?action=profiles}). */
export class LoadWorksheetProfiles {
	static readonly type: string = '[WorksheetApp] LoadWorksheetProfiles';
	constructor(public readonly appName: string) {}
}

/**
 * Load a profile's grid ({@code ?action=load}). Execute mode → computed values
 * (read-only); build mode ({@code &mode=build}) → raw formula text (editable).
 */
export class LoadWorksheet {
	static readonly type: string = '[WorksheetApp] LoadWorksheet';
	constructor(
		public readonly appName: string,
		public readonly profileCd: string,
		public readonly mode: WorksheetMode = 'execute'
	) {}
}

/**
 * Load an imported `.xlsx` into the grid as an UNSAVED draft profile — no HTTP,
 * no DB write. Shows the "modified, not saved" dot until {@link PersistWorksheetDraft}.
 */
export class SetWorksheetDraft {
	static readonly type: string = '[WorksheetApp] SetWorksheetDraft';
	constructor(
		public readonly profileCd: string,
		public readonly sheets: ReadonlyArray<WorksheetSheet>
	) {}
}

/** Persist the unsaved draft as a NEW profile ({@code POST ?action=save-as-new}) — the Update action. */
export class PersistWorksheetDraft {
	static readonly type: string = '[WorksheetApp] PersistWorksheetDraft';
	constructor(
		public readonly appName: string,
		public readonly profileCd: string,
		public readonly sheets: ReadonlyArray<WorksheetSaveSheet>
	) {}
}

/** Drop the unsaved draft (it was never persisted). */
export class DiscardWorksheetDraft {
	static readonly type: string = '[WorksheetApp] DiscardWorksheetDraft';
}

/** Persist the client-authored grid ({@code POST ?action=save}). */
export class SaveWorksheet {
	static readonly type: string = '[WorksheetApp] SaveWorksheet';
	constructor(
		public readonly appName: string,
		public readonly profileCd: string,
		public readonly sheets: ReadonlyArray<WorksheetSaveSheet>
	) {}
}

/** Live-refresh recompute of the active profile ({@code ?action=refresh}). */
export class RefreshWorksheet {
	static readonly type: string = '[WorksheetApp] RefreshWorksheet';
	/** True when the user clicked Refresh (failures surface a toast); false for
	 *  the background poll loop (failures stay silent so a transient blip doesn't
	 *  spam the user). */
	constructor(public readonly userInitiated: boolean = false) {}
}

/** Start/stop the live-refresh poll loop (UI scratch flag). */
export class SetWorksheetPolling {
	static readonly type: string = '[WorksheetApp] SetWorksheetPolling';
	constructor(public readonly polling: boolean) {}
}
